import React, { useState } from "react";
import { formatMoneyIntl } from "../../lib/formatMoney"; // relative: must REALLY resolve under Jest (no "@/" alias there)
import { CreditCard, Loader2, X } from "lucide-react";
import { api, extractApiError } from "@/lib/api";
import { useLanguage, useT } from "@/lib/LanguageContext";

/**
 * GiftCardField — the ONE gift-card input of every storefront (BUG-020).
 *
 * A CARD IS A PAYMENT INSTRUMENT, NOT A DISCOUNT, and this field is deliberately a
 * sibling of PromoField rather than a variant of it. A promo changes the PRICE; a card
 * changes WHO PAYS which part of it. Both can be present on one checkout, and the server
 * applies them in that order (promo first, then the card against the discounted total).
 *
 * WHAT IT IS AND IS NOT. It is a PREVIEW widget, exactly like PromoField. "Apply" calls
 * `GET /api/gift-cards/{code}/quote`, which describes the card as it stands right now and
 * spends nothing — the balance can move between this click and the click on "Pay" (the
 * recipient spends it elsewhere, a second checkout takes it), and the split is decided
 * server-side by `payments.service._apply_giftcard`, whose refusal is 402
 * GIFTCARD_DECLINED.
 *
 * TWO MODES, because pages differ in what they honestly know:
 *   `amountCents` GIVEN → "covers X · about Y left to pay". THE AMOUNT IS THE TOTAL THAT
 *       WILL BE CHARGED, tax included, as the server quoted it (BUG-048:
 *       `useCheckoutQuote`'s `total_charged_cents`). Every caller passes that now — the
 *       purchase panel, the booking summary, the course page. It used to be the page's
 *       displayed PRE-TAX price, which is why the residual was documented as a floor:
 *       the checkout charged the taxed total and the split moved. "About" survives for
 *       the reason it was always also true — a balance can be spent elsewhere between
 *       this click and the click on "Pay", and the server decides again
 *       (`payments.service._apply_giftcard`, 402 GIFTCARD_DECLINED).
 *   `amountCents` ABSENT or 0 (the quote has not landed, or is unreadable) → the card's
 *       BALANCE and nothing more. Inventing a residual against an amount this page does
 *       not know would be a number with no referent.
 *
 * THE SUB-MINIMUM NOTE is the one piece of arithmetic the buyer must see BEFORE paying:
 * Stripe refuses a charge under CA$0.50, so a card that would leave 30¢ has its
 * application capped and the last cents STAY ON THE CARD. The server flags it
 * (`residual_clamped`), and saying so here is what stops that being discovered as a
 * surprise on the payment page.
 *
 * WHAT THE PARENT OWNS. The accepted code string, via `value` / `onChange`. The parent
 * ships it in its own checkout payload (`gift_card_code`), so there is exactly one
 * request per purchase and no half-applied state to reconcile.
 *
 * Props:
 *   value        the accepted code (string, "" when none) — controlled by the parent.
 *   onChange     (code: string) => void, called with the accepted code or "" on removal.
 *   amountCents  the order this page knows about, in cents. Optional (see above).
 *   currency     ISO currency of the purchase, for rendering the amounts.
 *   disabled     the parent is busy (a checkout in flight).
 */
export default function GiftCardField({
  value,
  onChange,
  amountCents = 0,
  currency,
  disabled = false,
}) {
  const { lang } = useLanguage();
  const t = useT("public");
  const [draft, setDraft] = useState("");
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // The purchase's own currency, handed in by the page; never a default of this field's.
  const fmtAmount = (cents) => formatMoneyIntl(cents || 0, currency, lang);

  const apply = async () => {
    const code = draft.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get(
        `/gift-cards/${encodeURIComponent(code)}/quote`,
        { params: amountCents > 0 ? { amount_cents: amountCents } : {} },
      );
      if (!data?.spendable || !(data.balance_cents > 0)) {
        // "Not spendable" covers cancelled, refunded and fully redeemed alike — one
        // message, matching the server, which does not enumerate a practice's cards.
        setError(t("giftcard.empty"));
        return;
      }
      setQuote(data);
      onChange(data.code);
      setDraft("");
    } catch (e) {
      setError(e?.response?.status === 404
        ? t("giftcard.invalid")
        : extractApiError(e, t("giftcard.invalid")));
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    setQuote(null);
    setError("");
    onChange("");
  };

  if (value && quote) {
    const covers = Number(quote.applicable_cents || 0);
    const label = covers > 0
      ? t("giftcard.covers", {
          covered: fmtAmount(covers),
          due: fmtAmount(quote.remaining_due_cents || 0),
        })
      : t("giftcard.balance", { value: fmtAmount(quote.balance_cents || 0) });
    return (
      <div data-testid="giftcard-applied">
        <div className="flex items-center gap-2 rounded-xl border border-eh-border-soft bg-eh-cream px-3 py-2.5 text-sm">
          <CreditCard size={14} className="text-eh-sage-deep shrink-0" />
          <span className="font-mono text-eh-ink">{value}</span>
          <span className="text-eh-muted">· {label}</span>
          <button
            type="button"
            onClick={remove}
            disabled={disabled}
            aria-label={t("giftcard.remove")}
            className="ml-auto p-1 rounded-full text-eh-muted hover:text-eh-ink"
          >
            <X size={14} />
          </button>
        </div>
        {quote.residual_clamped && (
          <div className="mt-1 text-xs text-eh-muted" data-testid="giftcard-minimum">
            {t("giftcard.minimumResidual", {
              value: fmtAmount(quote.minimum_residual_cents || 50),
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="giftcard-field">
      <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">
        {t("giftcard.label")}
      </label>
      <div className="mt-1 flex gap-2">
        <input
          type="text"
          value={draft}
          disabled={disabled || busy}
          placeholder={t("giftcard.placeholder")}
          onChange={(e) => { setDraft(e.target.value.toUpperCase()); setError(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
          className="flex-1 bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm font-mono"
          data-testid="giftcard-input"
        />
        <button
          type="button"
          onClick={apply}
          disabled={disabled || busy || !draft.trim()}
          className="btn-secondary text-sm px-4"
          data-testid="giftcard-apply"
        >
          {busy ? <Loader2 className="animate-spin" size={14} /> : t("giftcard.apply")}
        </button>
      </div>
      {busy && <div className="mt-1 text-xs text-eh-muted">{t("giftcard.checking")}</div>}
      {error && (
        <div className="mt-1 text-xs text-red-600" data-testid="giftcard-error">{error}</div>
      )}
    </div>
  );
}
